import React from 'react';
import Article from '../components/Article';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CodeBlock from '../components/CodeBlock';

function EssentialCybersecurityPracticesForWebDevelopers() {
  const articleData = {
    id: 5,
    image: "https://images.unsplash.com/photo-1598520106830-8c45c2035460?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
    category: "Security",
    title: "Essential Cybersecurity Practices for Web Developers",
    excerpt: "Stay ahead of security threats with these proven strategies to protect your web applications.",
    tags: ["Security", "Web Development", "Cybersecurity", "Best Practices", "OWASP"],
    content: (
      <>
        <p>
          In today's interconnected digital landscape, web applications have become prime targets for cyberattacks. 
          As a web developer, your role in safeguarding user data and maintaining application security has never 
          been more critical. This article outlines essential cybersecurity practices that every web developer 
          should implement to protect their applications from increasingly sophisticated threats.
        </p>
        
        <h2>Understanding the Threat Landscape</h2>
        <p>
          Before diving into specific practices, it's important to understand the evolving threat landscape. 
          According to recent reports, web application attacks account for over 40% of all data breaches. 
          The most common vulnerabilities continue to align with the OWASP Top 10 list, which includes 
          injection attacks, broken authentication, sensitive data exposure, and cross-site scripting.
        </p>
        
        <p>
          With the average cost of a data breach now exceeding $4 million, security can no longer be 
          an afterthought—it must be integrated throughout the development lifecycle.
        </p>
        
        <h2>1. Implement Secure Authentication and Session Management</h2>
        <p>
          Authentication vulnerabilities remain one of the most common entry points for attackers. 
          Implementing robust authentication and session management is your first line of defense.
        </p>
        
        <h3>Use Modern Authentication Standards</h3>
        <p>
          Implement OAuth 2.0, OpenID Connect, or SAML for authentication when possible. These standards 
          have been rigorously tested and provide secure authentication flows.
        </p>
        
        <CodeBlock language="javascript" filename="OAuth2Setup.js">
{`// Example: Setting up OAuth 2.0 with Passport.js in Node.js
const passport = require('passport');
const OAuth2Strategy = require('passport-oauth2');

passport.use(new OAuth2Strategy({
    authorizationURL: 'https://provider.com/oauth2/authorize',
    tokenURL: 'https://provider.com/oauth2/token',
    clientID: process.env.CLIENT_ID,
    clientSecret: process.env.CLIENT_SECRET,
    callbackURL: "https://yourdomain.com/auth/callback"
  },
  function(accessToken, refreshToken, profile, cb) {
    User.findOrCreate({ providerUserId: profile.id }, (err, user) => {
      return cb(err, user);
    });
  }
));`}
        </CodeBlock>
        
        <h3>Implement Proper Password Handling</h3>
        <p>
          If you must handle passwords directly:
        </p>
        
        <ol className="workflow-steps">
          <li>Use strong, adaptive hashing algorithms like Argon2, bcrypt, or PBKDF2</li>
          <li>Enforce strong password policies (length, complexity, etc.)</li>
          <li>Implement account lockout after failed attempts</li>
          <li>Support multi-factor authentication</li>
        </ol>
        
        <CodeBlock language="javascript" filename="PasswordHandling.js">
{`// Example: Hashing passwords with bcrypt in Node.js
const bcrypt = require('bcrypt');
const saltRounds = 12; // Higher is more secure but slower

async function hashPassword(password) {
  try {
    const hashedPassword = await bcrypt.hash(password, saltRounds);
    return hashedPassword;
  } catch (error) {
    throw new Error('Error hashing password');
  }
}

async function verifyPassword(password, hashedPassword) {
  try {
    return await bcrypt.compare(password, hashedPassword);
  } catch (error) {
    throw new Error('Error verifying password');
  }
}`}
        </CodeBlock>
        
        <h3>Secure Session Management</h3>
        <p>
          Proper session management is crucial for maintaining security after authentication:
        </p>
        
        <ol className="workflow-steps">
          <li>Generate secure, random session IDs</li>
          <li>Set appropriate session timeouts</li>
          <li>Regenerate session IDs after login</li>
          <li>Implement secure cookie settings</li>
        </ol>
        
        <CodeBlock language="javascript" filename="SessionManagement.js">
{`// Example: Secure cookie configuration in Express
app.use(session({
  secret: process.env.SESSION_SECRET,
  cookie: {
    httpOnly: true,     // Prevents client-side JS from reading the cookie
    secure: true,       // Only transmit over HTTPS
    sameSite: 'strict', // Prevents CSRF attacks
    maxAge: 3600000     // Session timeout (1 hour)
  },
  resave: false,
  saveUninitialized: false
}));`}
        </CodeBlock>
        
        <h2>2. Protect Against Injection Attacks</h2>
        <p>
          Injection attacks, particularly SQL injection and Cross-Site Scripting (XSS), 
          remain prevalent despite being well-understood vulnerabilities.
        </p>
        
        <h3>Preventing SQL Injection</h3>
        <p>
          SQL injection occurs when untrusted data is sent to an interpreter as part of a command or query.
        </p>
        
        <CodeBlock language="javascript" filename="SQLInjection.js">
{`// Vulnerable code - DON'T DO THIS
const query = "SELECT * FROM users WHERE username = '" + username + "'";

// Secure code using parameterized queries
// Node.js with MySQL
connection.query(
  'SELECT * FROM users WHERE username = ?', 
  [username],
  function(error, results, fields) {
    // Handle results
  }
);

// Using an ORM like Sequelize
User.findAll({
  where: {
    username: username
  }
});`}
        </CodeBlock>
        
        <h3>Preventing Cross-Site Scripting (XSS)</h3>
        <p>
          XSS attacks inject malicious scripts into trusted websites. To prevent XSS:
        </p>
        
        <ol className="workflow-steps">
          <li>Escape output based on context (HTML, JavaScript, CSS, URL)</li>
          <li>Use Content Security Policy (CSP) headers</li>
          <li>Sanitize user input</li>
          <li>Use modern frameworks that automatically escape output</li>
        </ol>
        
        <CodeBlock language="javascript" filename="XSSPrevention.js">
{`// Example: Setting up CSP headers in Express
app.use((req, res, next) => {
  res.setHeader(
    'Content-Security-Policy',
    "default-src 'self'; script-src 'self' https://trusted-cdn.com; style-src 'self' https://trusted-cdn.com; img-src 'self' data: https://trusted-cdn.com"
  );
  next();
});

// Example: Sanitizing user input with DOMPurify in the browser
const sanitizedHTML = DOMPurify.sanitize(userProvidedHTML);
document.getElementById('content').innerHTML = sanitizedHTML;`}
        </CodeBlock>
        
        <h2>3. Implement Secure Data Transmission and Storage</h2>
        <p>
          Protecting data in transit and at rest is fundamental to web application security.
        </p>
        
        <h3>HTTPS Everywhere</h3>
        <p>
          All web applications should use HTTPS to encrypt data in transit:
        </p>
        
        <ol className="workflow-steps">
          <li>Obtain proper SSL/TLS certificates</li>
          <li>Configure secure TLS protocols and cipher suites</li>
          <li>Implement HTTP Strict Transport Security (HSTS)</li>
          <li>Redirect HTTP to HTTPS</li>
        </ol>
        
        <CodeBlock language="javascript" filename="HTTPSSetup.js">
{`// Example: HTTPS redirection and HSTS in Express
app.use((req, res, next) => {
  if (!req.secure && process.env.NODE_ENV === 'production') {
    return res.redirect('https://' + req.headers.host + req.url);
  }
  next();
});

app.use((req, res, next) => {
  res.setHeader('Strict-Transport-Security', 'max-age=63072000; includeSubDomains; preload');
  next();
});`}
        </CodeBlock>
        
        <h3>Sensitive Data Protection</h3>
        <p>
          When handling sensitive data:
        </p>
        
        <ol className="workflow-steps">
          <li>Encrypt sensitive data at rest using strong encryption</li>
          <li>Use environment variables or secure vaults for storing secrets</li>
          <li>Implement proper key management</li>
          <li>Minimize collection and retention of sensitive data</li>
        </ol>
        
        <CodeBlock language="javascript" filename="DataEncryption.js">
{`// Example: Encrypting sensitive data with Node.js crypto
const crypto = require('crypto');

// Encryption function
function encryptData(text, masterKey) {
  const iv = crypto.randomBytes(16);
  const key = crypto.scryptSync(masterKey, 'salt', 32);
  const cipher = crypto.createCipheriv('aes-256-gcm', key, iv);
  
  let encrypted = cipher.update(text, 'utf8', 'hex');
  encrypted += cipher.final('hex');
  
  const authTag = cipher.getAuthTag().toString('hex');
  return {
    iv: iv.toString('hex'),
    encrypted: encrypted,
    authTag: authTag
  };
}

// Decryption function
function decryptData(encData, masterKey) {
  const key = crypto.scryptSync(masterKey, 'salt', 32);
  const decipher = crypto.createDecipheriv(
    'aes-256-gcm', 
    key, 
    Buffer.from(encData.iv, 'hex')
  );
  
  decipher.setAuthTag(Buffer.from(encData.authTag, 'hex'));
  
  let decrypted = decipher.update(encData.encrypted, 'hex', 'utf8');
  decrypted += decipher.final('utf8');
  
  return decrypted;
}`}
        </CodeBlock>
        
        <h2>4. Implement Proper Access Controls</h2>
        <p>
          Access control vulnerabilities can lead to unauthorized data access or administrative actions.
        </p>
        
        <h3>Follow Access Control Principles</h3>
        <ol className="workflow-steps">
          <li>Implement least privilege principle</li>
          <li>Deny by default</li>
          <li>Check access controls on server-side (never trust client-side)</li>
          <li>Implement role-based access control (RBAC)</li>
        </ol>
        
        <CodeBlock language="javascript" filename="AccessControl.js">
{`// Example: Express middleware for role-based access control
function checkRole(role) {
  return (req, res, next) => {
    if (!req.user) {
      return res.status(401).json({ message: 'Unauthorized' });
    }
    
    if (req.user.role !== role) {
      return res.status(403).json({ message: 'Forbidden' });
    }
    
    next();
  };
}

// Usage in routes
app.get('/admin/users', checkRole('admin'), (req, res) => {
  // Only admin users can access this route
});

app.get('/profile', checkRole('user'), (req, res) => {
  // Only authenticated users can access this route
});`}
        </CodeBlock>
        
        <h2>5. Protect Against Cross-Site Request Forgery (CSRF)</h2>
        <p>
          CSRF attacks trick users into performing unwanted actions on websites where they're authenticated.
        </p>
        
        <CodeBlock language="javascript" filename="CSRFProtection.js">
{`// Example: CSRF protection in Express using csurf
const csrf = require('csurf');
const csrfProtection = csrf({ cookie: true });

app.use(cookieParser()); // Required for cookie-based CSRF

// Apply CSRF protection to routes that change state
app.get('/form', csrfProtection, (req, res) => {
  // Pass the CSRF token to the view
  res.render('form', { csrfToken: req.csrfToken() });
});

app.post('/process', csrfProtection, (req, res) => {
  // CSRF token is automatically validated
  // Process the form...
});

// In your form template
// <input type="hidden" name="_csrf" value="{{ csrfToken }}">`}
        </CodeBlock>
        
        <h2>6. Implement Security Headers</h2>
        <p>
          HTTP security headers provide an additional layer of security for your web applications.
        </p>
        
        <CodeBlock language="javascript" filename="SecurityHeaders.js">
{`// Example: Setting security headers in Express using helmet
const helmet = require('helmet');
app.use(helmet()); // Applies a set of sensible default headers

// Or configure specific headers
app.use(
  helmet({
    contentSecurityPolicy: {
      directives: {
        defaultSrc: ["'self'"],
        scriptSrc: ["'self'", "trusted-scripts.com"],
        // ...other directives
      },
    },
    referrerPolicy: { policy: 'same-origin' },
    // ...other configurations
  })
);`}
        </CodeBlock>
        
        <p>
          Key security headers include:
        </p>
        <ol className="workflow-steps">
          <li>Content-Security-Policy: Controls resources the browser is allowed to load</li>
          <li>X-Content-Type-Options: Prevents MIME type sniffing</li>
          <li>X-Frame-Options: Prevents clickjacking</li>
          <li>Referrer-Policy: Controls information in the Referer header</li>
          <li>Permissions-Policy: Controls which browser features can be used</li>
        </ol>
        
        <h2>7. Regular Security Testing and Monitoring</h2>
        <p>
          Implementing security controls is only effective when combined with continuous testing and monitoring.
        </p>
        
        <h3>Security Testing Approaches</h3>
        <ol className="workflow-steps">
          <li><strong>Static Application Security Testing (SAST):</strong> Analyze source code for security vulnerabilities</li>
          <li><strong>Dynamic Application Security Testing (DAST):</strong> Test running applications for vulnerabilities</li>
          <li><strong>Dependency Scanning:</strong> Check dependencies for known vulnerabilities</li>
          <li><strong>Penetration Testing:</strong> Simulate attacks to identify vulnerabilities</li>
        </ol>
        
        <CodeBlock language="yaml" filename="SecurityScan.yml">
{`// Example: npm audit to check for vulnerable dependencies
npm audit

// Example: Adding security testing to CI/CD pipeline (GitHub Actions)
name: Security Scan

on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  security:
    runs-on: ubuntu-latest
    steps:
    - uses: actions/checkout@v2
    - name: Run SAST scan
      run: |
        npm install
        npm run security:sast
    - name: Check dependencies
      run: npm audit --production`}
        </CodeBlock>
        
        <h3>Logging and Monitoring</h3>
        <p>
          Implement comprehensive logging and monitoring to detect and respond to security incidents:
        </p>
        <ol className="workflow-steps">
          <li>Log security-relevant events (authentication, access control, etc.)</li>
          <li>Implement centralized log management</li>
          <li>Set up alerts for suspicious activities</li>
          <li>Regularly review logs and alerts</li>
        </ol>
        
        <CodeBlock language="javascript" filename="Logging.js">
{`// Example: Logging security events in Node.js
const winston = require('winston');

const logger = winston.createLogger({
  level: 'info',
  format: winston.format.json(),
  defaultMeta: { service: 'user-service' },
  transports: [
    new winston.transports.File({ filename: 'security-events.log', level: 'warn' }),
    new winston.transports.Console({ format: winston.format.simple() })
  ]
});

// Log authentication attempts
function logAuthAttempt(username, success, ip) {
  logger.log({
    level: success ? 'info' : 'warn',
    message: \`Authentication attempt: \${username}\`,
    metadata: {
      event: 'authentication',
      username,
      success,
      ip,
      timestamp: new Date().toISOString()
    }
  });
}`}
        </CodeBlock>
        
        <h2>8. Keep Dependencies Updated</h2>
        <p>
          Vulnerabilities in third-party dependencies are a common attack vector. Regularly updating 
          dependencies is essential for security.
        </p>
        
        <CodeBlock language="yaml" filename="Dependabot.yml">
{`// Update dependencies to latest secure versions
npm update

// Find outdated packages
npm outdated

// Use tools like Dependabot to automate updates
// Example .github/dependabot.yml
version: 2
updates:
  - package-ecosystem: "npm"
    directory: "/"
    schedule:
      interval: "weekly"
    open-pull-requests-limit: 10`}
        </CodeBlock>
        
        <h2>9. Implement Proper Error Handling</h2>
        <p>
          Improper error handling can leak sensitive information to attackers.
        </p>
        
        <CodeBlock language="javascript" filename="ErrorHandling.js">
{`// Example: Error handling middleware in Express
app.use((err, req, res, next) => {
  // Log detailed error information for debugging
  console.error(err);
  
  // Send generic error message to client
  res.status(500).json({ 
    message: 'An unexpected error occurred'
  });
});`}
        </CodeBlock>
        
        <h2>10. Practice Secure DevOps (DevSecOps)</h2>
        <p>
          Integrate security throughout the development lifecycle:
        </p>
        <ol className="workflow-steps">
          <li>Implement security gates in CI/CD pipelines</li>
          <li>Automate security testing</li>
          <li>Use infrastructure as code with security best practices</li>
          <li>Conduct security code reviews</li>
        </ol>
        
        <h2>Conclusion</h2>
        <p>
          Security is not a feature—it's a continuous process that must be integrated into every aspect 
          of web development. By implementing these essential cybersecurity practices, you can significantly 
          reduce the risk of security breaches and protect both your application and your users' data.
        </p>
        
        <p>
          Remember that security threats evolve constantly, so staying informed about emerging vulnerabilities 
          and attack vectors is crucial. Join security communities, follow security blogs, and participate in 
          training to keep your security knowledge current.
        </p>
        
        <p>
          With a security-first mindset and the practices outlined in this article, you'll be well-equipped 
          to develop and maintain secure web applications in today's challenging threat landscape.
        </p>
        
      </>
    )
  };

  return (
    <div className="article-page">
      <Navbar />
      <main>
        <Article article={articleData} />
      </main>
      <Footer />
    </div>
  );
}

export default EssentialCybersecurityPracticesForWebDevelopers;
